// components
import React from 'react';
import { MenuItem } from '@mui/material';

import GridPageAbstract from '../abstract/GridPageAbstract';
import CreateTransactionDialog from '../dialogs/CreateTransactionDialog';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import { centToCurrency } from '../../utils/formatNumber';
import { fDateTime } from '../../utils/formatTime';
import {
    TG_MESSAGE_DIRECTION,
    TG_SESSION_MESSAGE_STATUS
} from '../../constants/tgSessionStatus';

/** What a message core could not attribute is left as: money that moved, for an admin. */
const LEFT_FOR_AN_ADMIN = ['unmatched', 'ambiguous'];

/**
 * The transactions an appeal may change (the backend's AppealRules): decided ones.
 * An open one is decided with Confirm on its own grid.
 */
const DECIDED_STATES = {
    payin: ['processed', 'refused', 'cancelled', 'expired'],
    payout: ['withdrawed', 'refused', 'withdraw_cancelled', 'cancelled']
};

/** Whether an admin may credit a transaction with this message's amount by appeal. */
export function appealableMessage(row) {
    return (
        row?.direction === 'in' &&
        LEFT_FOR_AN_ADMIN.includes(row?.status) &&
        row?.amount != null &&
        !!DECIDED_STATES[row?.amountKind]
    );
}

/**
 * The states a transaction the message may credit can be in. A payin processed
 * already is not one: the money the message reports is not what paid it.
 */
const CANDIDATE_STATES = {
    payin: DECIDED_STATES.payin.filter((state) => state !== 'processed'),
    payout: DECIDED_STATES.payout
};

/**
 * Filters for the settings rows reading the message's chat, in two steps: the chat
 * row for its session and peer, then the rows linked to it. A transaction's callback
 * row shows no chat, so the picker cannot filter transactions by it directly — it
 * did until 2026-11-03, was refused, and opened empty. peerId goes as a number: the
 * API filters a Long, and a string matches nothing.
 */
export const chatOfMessagePrefilter = (message) => [
    { field: 'tgSession.id', op: '=', value: message.tgSession?.id },
    { field: 'peerId', op: '=', value: Number(message.peerId) }
];

export const readersOfChatsPrefilter = (chatIds) => [{ field: 'tgSessionChat.id', op: '@', value: chatIds }];

/**
 * The transactions the appeal's picker offers: those of the message's kind that it
 * may still credit, created to be confirmed from its chat (`readerIds`, the settings
 * rows reading it) — the backend refuses any other — and, for income whose card
 * points at a wallet, that wallet's: the card the money reached. Not for a debit: a
 * payout whose card is named at confirmation (V33) has none until then.
 */
/**
 * What tells an appeal's candidates apart in the picker, above each one's uuid: the
 * amount its payer was asked to send (a payin's payable amount, on a card shared by
 * amount, else the requested one), its state, when it was made and the merchant's id.
 */
export const describeAppealCandidate = (item) =>
    [
        centToCurrency(item.payableAmount ?? item.requestedAmount),
        item.state ? i18n.t(`enums.${item.state}`) : null,
        fDateTime(item.dateCreated, 'd MMM yyyy HH:mm'),
        item.merchantTransactionId
    ]
        .filter(Boolean)
        .join(' · ');

export function appealCandidatesPrefilter(message, readerIds) {
    const payin = message.amountKind === 'payin';
    const prefilter = [
        { field: 'state', op: '@', value: CANDIDATE_STATES[message.amountKind] },
        { field: `${payin ? 'payinCallbackSettings' : 'payoutCallbackSettings'}.id`, op: '@', value: readerIds }
    ];
    if (payin && message.wallet?.id != null) {
        prefilter.push({ field: 'wallet.id', op: '=', value: message.wallet.id });
    }
    return prefilter;
}

/**
 * Messages received in watched chats, and messages sent through a session.
 *
 * Read-only: rows are written by core as tg-client reports them, and
 * permissions.yml denies everything but read. Newest first comes from the
 * default sort (id desc), which is arrival order.
 *
 * Each keeps what its settings row's rule read (V32): the amount, the card's digits
 * and the wallet they point at. One core left unmatched or ambiguous can credit a
 * decided payin or payout by appeal, for exactly its amount: "File an appeal" picks
 * the transaction and files it pending, for an admin to approve on Appeals.
 */
export default class TgSessionMessagesPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicTgSessionMessages';
        this.Server = this.props.context.getState().server.publicTgSessionMessages;
        this.MenuDisplayType = 'menu';
        // A payin's or payout's uuid finds the message that decided it.
        this.searchFields = ['text', 'payinUuid', 'payoutUuid'];
        this.disableGlobalEditor = true;
        this.disableGlobalDelete = true;
        this.disableAdd = true;
        // Appeals are another controller's rows: filing one is a create there.
        this.Appeals = this.props.context.getState().server.publicAppeals;
        // Which settings rows read a message's chat: what the appeal's picker filters by.
        this.Chats = this.props.context.getState().server.publicTgSessionChats;
        this.GatewaySettings = this.props.context.getState().server.publicGatewaySettings;
        // Seeds the session filter when arriving from a session's "Messages".
        this.hasCustomLogicOnMount = true;

        const defaultHead = [
            { id: 'dateCreated', label: 'dateCreated', alignRight: false, cell: 'DateTime', displayGrid: true, disableEditor: true },
            {
                id: 'tgSessionId',
                label: 'tgSession',
                alignRight: false,
                cell: 'Select',
                // `from` is what loads the session list the filter offers.
                from: 'publicTgSessions',
                object: 'tgSession',
                show: 'name',
                filterCell: 'Context',
                filterBy: 'publicTgSessions',
                displayGrid: true,
                disableEditor: true,
                customSort: 'tgSession.name'
            },
            {
                id: 'direction',
                label: 'direction',
                alignRight: false,
                cell: 'Items',
                items: TG_MESSAGE_DIRECTION,
                displayGrid: true,
                disableEditor: true
            },
            // Ids as Text: they exceed 2^53, travel as strings, and are compared by
            // eye against a chat's peer id.
            { id: 'peerId', label: 'peerId', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'senderId', label: 'senderId', alignRight: false, cell: 'Text', displayGrid: false, disableEditor: true },
            { id: 'tgMessageId', label: 'tgMessageId', alignRight: false, cell: 'Text', displayGrid: false, disableEditor: true },
            { id: 'text', label: 'messageText', alignRight: false, cell: 'BigText', displayGrid: true, disableEditor: true },
            // Telegram's own date for the message — what a payin's match window is
            // measured against, not when it reached us.
            { id: 'sentAt', label: 'sentAt', alignRight: false, cell: 'DateTime', displayGrid: true, disableEditor: true },
            {
                id: 'status',
                label: 'status',
                alignRight: false,
                cell: 'State',
                items: TG_SESSION_MESSAGE_STATUS,
                displayGrid: true,
                disableEditor: true
            },
            { id: 'statusMessage', label: 'statusMessage', alignRight: false, cell: 'BigText', displayGrid: true, disableEditor: true },
            // What the row's rule read: income credits a payin, a debit a payout.
            { id: 'amount', label: 'amount', alignRight: true, cell: 'Amount', blankWhenNull: true, displayGrid: true, disableEditor: true },
            { id: 'account', label: 'cardTail', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            // For income naming a card: the wallet it reached, whose payins to look at.
            { id: 'walletId', label: 'wallet', alignRight: false, cell: 'Number', prefix: '#', blankWhenNull: true, displayGrid: true, disableEditor: true },
            {
                id: 'gatewayId',
                label: 'gateway',
                alignRight: false,
                cell: 'Select',
                object: 'gateway',
                show: 'name',
                displayGrid: true,
                disableEditor: true,
                customSort: 'gateway.name'
            },
            // What the message decided: a payin by its credit, or a payout by its
            // debit (a tg_session payout_callback row). Each opens its grid
            // searching for it.
            {
                id: 'payinUuid',
                label: 'payinUuid',
                alignRight: false,
                cell: 'Link',
                linkTo: '/app/payin-instances',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'payoutUuid',
                label: 'payoutUuid',
                alignRight: false,
                cell: 'Link',
                linkTo: '/app/payout-instances',
                displayGrid: true,
                disableEditor: true
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            appealMessage: null,
            appealReaderIds: []
        };
    }

    /** The ids of the settings rows reading the message's chat, archived ones included. */
    async readersOfChat(message) {
        const chats = await this.Chats.search(100, [], [], chatOfMessagePrefilter(message));
        if (chats?.error !== 'OK') throw new Error(chats?.message || 'the chat could not be read');
        const chatIds = (chats.data || []).map((chat) => chat.id);
        if (chatIds.length === 0) return [];
        const rows = await this.GatewaySettings.search(100, [], [], readersOfChatsPrefilter(chatIds));
        if (rows?.error !== 'OK') throw new Error(rows?.message || 'the settings rows could not be read');
        return (rows.data || []).map((row) => row.id);
    }

    openAppeal = async () => {
        const row = this.getMenuClickItem();
        this.handleCloseMenu();
        try {
            const readerIds = await this.readersOfChat(row);
            this.setState({ appealMessage: row, appealReaderIds: readerIds });
        } catch (error) {
            this.props.context.update({
                alertError: { message: error.message, type: 'snackbar', severity: 'error' }
            });
        }
    };

    closeAppeal = () => {
        this.setState({ appealMessage: null, appealReaderIds: [] });
    };

    appealFiled = (appeal) => {
        this.props.context.update({
            alertError: {
                message: i18n.t('text.appealFiledFromMessage', { id: appeal?.id }),
                type: 'snackbar',
                severity: 'success'
            }
        });
        this.retrieveData();
    };

    renderCustomMenuItems(id) {
        const row = this.List.find((item) => item.id === id);
        // An array, not a fragment: hasMenuItems counts custom items with
        // Array.isArray(...) && length > 0.
        if (!appealableMessage(row) || !this.allowedPermission('create', 'publicAppeals')) return [];
        return [
            <MenuItem key={`${id}-appeal`} onClick={this.openAppeal}>
                <Iconify icon="eva:file-add-outline" sx={{ mr: 2 }} />
                {i18n.t('buttons.fileAppealFromMessage')}
            </MenuItem>
        ];
    }

    renderCustom() {
        const message = this.state.appealMessage;
        if (!message) return null;

        const kind = message.amountKind;
        const transactionId = kind === 'payin' ? 'payinId' : 'payoutId';
        return (
            <CreateTransactionDialog
                dialogOpen
                type="appeal-from-message"
                id={message.id}
                Head={[
                    { id: 'messageText', label: 'messageText', cell: 'BigText', onlyRead: true, default: message.text },
                    {
                        id: 'messageAmount',
                        label: 'amount',
                        cell: 'Text',
                        onlyRead: true,
                        default: `${centToCurrency(message.amount)}${message.account ? ` · *${message.account}` : ''}`
                    },
                    {
                        id: transactionId,
                        label: kind,
                        cell: 'ServerSearch',
                        required: true,
                        searchController: kind === 'payin' ? 'payinInstances' : 'payoutInstances',
                        // A uuid matches only whole; the merchant's id matches in part.
                        searchFields: ['uuid', 'merchantTransactionId', 'gatewayTransactionId'],
                        show: 'uuid',
                        limit: 10,
                        sort: [{ field: 'id', direction: 'DESC' }],
                        prefilter: appealCandidatesPrefilter(message, this.state.appealReaderIds),
                        optionDetails: describeAppealCandidate,
                        helperText: 'appealFromMessageTransaction'
                    },
                    { id: 'reason', label: 'reason', cell: 'Text', required: true },
                    { id: 'description', label: 'description', cell: 'BigText' }
                ]}
                dialogTitle="dialogs.fileAppealFromMessage"
                saveLabel="buttons.fileAppealFromMessage"
                enablePrevData={false}
                // No amount and no state: the backend takes the message's.
                server={(id, body) =>
                    this.Appeals.add({
                        kind,
                        [transactionId]: body[transactionId],
                        tgSessionMessageId: message.id,
                        reason: body.reason,
                        description: body.description || null
                    })
                }
                onSuccess={this.appealFiled}
                onClose={this.closeAppeal}
            />
        );
    }

    /**
     * Arriving from a session's row: filter to that session.
     *
     * Put in the same filter group the toolbar's session filter writes, so it
     * shows as a chip and in the filter control and is removed the same way —
     * not as a hidden prefilter that the page could not explain.
     */
    async customLogicOnMount() {
        const tgSessionId = this.props.location?.state?.tgSessionId;
        if (!tgSessionId) return;

        this.fs
            .clearGroup('prefilterFilter')
            .addGroup([{ field: 'tgSession.id', op: '@', value: [tgSessionId] }], 'prefilterFilter');
        await this.setStateAsync({ filterActive: true, page: 0 });
    }

    // Only the page that arrived is read, and `append` ("Load more" on a phone) passed
    // on: rows already shown were read when they came, and reading them again would
    // scale a numeric sentAt a second time.
    processListData(data, append = false) {
        // sentAt is epoch seconds on tg-client's side of the wire. Should it ever
        // reach this grid that way, a bare number would be read as milliseconds
        // and land in January 1970.
        const rows = data.map((row) => ({
            ...row,
            sentAt: typeof row.sentAt === 'number' ? row.sentAt * 1000 : row.sentAt,
            walletId: row.wallet?.id ?? null
        }));
        super.processListData(rows, append);
    }
}
